// Page Artisans Mobile - Design unifié avec filtres
// Header simple "Artisans" + Onglets Tous/Filtre/Devis gratuit
// Formulaire Devis Gratuit intégré inline (pas de navigation externe)

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Star, MapPin, Phone, ChevronRight, Filter, Loader2,
  X, Clock, TrendingUp, ArrowRight, RefreshCw, FileText, Award,
  Building2, CheckCircle, Users, Briefcase, SlidersHorizontal, Gift,
  ArrowLeft, CheckCircle2, Wrench, Zap, Droplets, Paintbrush, Home,
  Hammer, Flame, Waves, Sun, Wind, Truck, Shovel, HardHat, Fence, Leaf,
  Euro, Shield, Mail, Send
} from 'lucide-react';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';
import ArtisansMapView from '../../components/maps/ArtisansMapView';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de travaux pour le formulaire Devis Gratuit
const TYPES_TRAVAUX = [
  { id: 'renovation', label: 'Rénovation', icon: Home, color: '#1E40AF', description: 'Salle de bain, cuisine...' },
  { id: 'construction', label: 'Construction', icon: Building2, color: '#10B981', description: 'Maison, extension...' },
  { id: 'plomberie', label: 'Plomberie', icon: Droplets, color: '#0EA5E9', description: 'Fuite, installation...' },
  { id: 'electricite', label: 'Électricité', icon: Zap, color: '#F59E0B', description: 'Tableau, prises...' },
  { id: 'peinture', label: 'Peinture', icon: Paintbrush, color: '#EC4899', description: 'Intérieur, extérieur...' },
  { id: 'menuiserie', label: 'Menuiserie', icon: Hammer, color: '#8B5CF6', description: 'Portes, fenêtres...' },
  { id: 'chauffage', label: 'Chauffage', icon: Flame, color: '#EF4444', description: 'Chaudière, PAC...' },
  { id: 'piscine', label: 'Piscine', icon: Waves, color: '#06B6D4', description: 'Construction, entretien...' },
  { id: 'solaire', label: 'Solaire', icon: Sun, color: '#FBBF24', description: 'Panneaux, batterie...' },
  { id: 'toiture', label: 'Toiture', icon: HardHat, color: '#78716C', description: 'Couverture, isolation...' },
  { id: 'maconnerie', label: 'Maçonnerie', icon: Shovel, color: '#A3A3A3', description: 'Murs, fondations...' },
  { id: 'jardin', label: 'Jardin', icon: Leaf, color: '#22C55E', description: 'Aménagement, entretien...' },
  { id: 'cloture', label: 'Clôture', icon: Fence, color: '#92400E', description: 'Portail, grillage...' },
  { id: 'demenagement', label: 'Déménagement', icon: Truck, color: '#6366F1', description: 'Transport, montage...' },
  { id: 'climatisation', label: 'Climatisation', icon: Wind, color: '#38BDF8', description: 'Installation, dépannage...' },
  { id: 'autre', label: 'Autre', icon: Wrench, color: '#71717A', description: 'Autre type de travaux...' },
];

// Urgences
const URGENCES = [
  { id: 'urgent', label: 'Urgent', sublabel: 'Sous 48h', color: '#EF4444' },
  { id: 'rapide', label: 'Rapide', sublabel: 'Sous 1 semaine', color: '#F59E0B' },
  { id: 'normal', label: 'Normal', sublabel: 'Sous 1 mois', color: '#10B981' },
  { id: 'flexible', label: 'Flexible', sublabel: 'Pas de rush', color: '#6366F1' },
];

// Budgets
const BUDGETS = [
  { id: 'small', label: '< 1 000€', range: '< 1k€' },
  { id: 'medium', label: '1k - 5k€', range: '1k - 5k€' },
  { id: 'large', label: '5k - 15k€', range: '5k - 15k€' },
  { id: 'xlarge', label: '15k - 50k€', range: '15k - 50k€' },
  { id: 'premium', label: '> 50k€', range: '> 50k€' },
];

// Métiers avec emojis pour la recherche
const METIERS_SEARCH = [
  { query: 'Plombier', icon: '💧', type: 'métier' },
  { query: 'Électricien', icon: '⚡', type: 'métier' },
  { query: 'Maçon', icon: '🧱', type: 'métier' },
  { query: 'Carreleur', icon: '🔲', type: 'métier' },
  { query: 'Peintre', icon: '🎨', type: 'métier' },
  { query: 'Menuisier', icon: '🔨', type: 'métier' },
  { query: 'Couvreur', icon: '🏠', type: 'métier' },
  { query: 'Chauffagiste', icon: '🔥', type: 'métier' },
  { query: 'Pisciniste', icon: '🏊', type: 'métier' },
  { query: 'Paysagiste', icon: '🌿', type: 'métier' },
];

// Mock data artisans (même que la page principale)
const MOCK_ARTISANS = [
  {
    id: 'artisan-1',
    shop_name: 'Plomberie Martin & Fils',
    metier: 'Plombier',
    city: 'Lyon',
    is_verified: true,
    rating_average: 4.9,
    reviews_count: 127,
    cover_image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
    is_5_stars: true,
    is_artisan_du_mois: false,
  },
  {
    id: 'artisan-2',
    shop_name: 'Élec Pro Méditerranée',
    metier: 'Électricien',
    city: 'Marseille',
    is_verified: true,
    rating_average: 4.8,
    reviews_count: 89,
    cover_image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400',
    is_5_stars: true,
    is_artisan_du_mois: true,
  },
  {
    id: 'artisan-3',
    shop_name: 'Maçonnerie Dupont',
    metier: 'Maçon',
    city: 'Toulouse',
    is_verified: true,
    rating_average: 4.7,
    reviews_count: 64,
    cover_image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400',
    is_5_stars: false,
    is_artisan_du_mois: false,
  },
  {
    id: 'artisan-4',
    shop_name: 'Peinture Leroy',
    metier: 'Peintre',
    city: 'Paris',
    is_verified: true,
    rating_average: 4.9,
    reviews_count: 203,
    cover_image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400',
    is_5_stars: true,
    is_artisan_du_mois: true,
  },
];

// Carte Artisan (tous les artisans sont vérifiés par défaut sur la plateforme)
function ArtisanCard({ artisan, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
      onClick={() => onClick(artisan)}
    >
      {/* Image */}
      <div className="relative h-32">
        <img 
          src={artisan.cover_image} 
          alt={artisan.shop_name}
          className="w-full h-full object-cover"
        />
        {/* Rating */}
        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded-full flex items-center gap-1">
          <Star size={12} className="text-yellow-400 fill-yellow-400" />
          <span className="text-white text-xs font-bold">{artisan.rating_average}</span>
          <span className="text-white/70 text-[10px]">({artisan.reviews_count})</span>
        </div>
      </div>
      
      {/* Infos */}
      <div className="p-3">
        <h3 className="font-semibold text-sm text-gray-900 truncate">{artisan.shop_name}</h3>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-orange-600 font-medium">{artisan.metier}</span>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <MapPin size={10} />
            {artisan.city}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export default function MobileArtisansPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [artisans, setArtisans] = useState(MOCK_ARTISANS);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Onglet principal : produits, prestations, carte
  const [mainTab, setMainTab] = useState('prestations'); // produits, prestations, carte
  
  // État pour afficher la carte inline
  const [showMapInline, setShowMapInline] = useState(false);
  
  // État pour l'onglet Produits
  const [boutiquesProducts, setBoutiquesProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchProductQuery, setSearchProductQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  
  // Géolocalisation automatique au chargement
  const [userCoords, setUserCoords] = useState(null);
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {} // refus silencieux, aucune erreur affichée
      );
    }
  }, []);

  // Filtres - Ouvrir devis si paramètre URL présent
  const initialFilterTab = searchParams.get('devis') === 'true' ? 'devis' : 'tous';
  const [filterTab, setFilterTab] = useState(initialFilterTab);
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFullFilters, setShowFullFilters] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchInputRef = useRef(null);
  
  // État des filtres pour UnifiedFilterSystem
  const [activeFilters, setActiveFilters] = useState({});
  
  // ============ ÉTATS FORMULAIRE DEVIS INLINE ============
  const [devisStep, setDevisStep] = useState(1);
  const [devisFormData, setDevisFormData] = useState({
    type: '',
    description: '',
    urgence: '',
    budget: '',
    ville: '',
    codePostal: '',
    nom: '',
    telephone: '',
    email: '',
  });
  const [isSubmittingDevis, setIsSubmittingDevis] = useState(false);
  const [devisSuccess, setDevisSuccess] = useState(false);
  const totalDevisSteps = 5;
  
  // Charger les recherches récentes
  useEffect(() => {
    const saved = localStorage.getItem('artisans_recent_searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved).slice(0, 5));
      } catch {}
    }
  }, []);
  
  // Focus sur l'input quand la recherche s'ouvre
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  // Sauvegarder une recherche
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try {
      localStorage.setItem('artisans_recent_searches', JSON.stringify(updated));
    } catch {}
  };
  
  // Effacer les recherches récentes
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('artisans_recent_searches'); } catch {}
  };
  
  // Recherche boutiques produits
  const searchBoutiquesProducts = async (query) => {
    if (!query || !query.trim()) {
      setBoutiquesProducts([]);
      return;
    }
    
    setLoadingProducts(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      // Endpoint réel du backend en ligne : /api/boutique (au singulier)
      const response = await fetch(`${API}/api/boutique?search=${encodeURIComponent(query.trim())}&per_page=20`);
      if (response.ok) {
        const data = await response.json();
        setBoutiquesProducts(data.boutiques || []);
      }
    } catch (error) {
      console.error('Erreur recherche boutiques produits:', error);
    } finally {
      setLoadingProducts(false);
    }
  };
  
  // Exécuter la recherche produits
  const handleProductSearchSubmit = (query) => {
    if (query && query.trim()) {
      setSearchProductQuery(query.trim());
      searchBoutiquesProducts(query.trim());
    }
    setShowProductSearch(false);
  };
  
  // Exécuter la recherche
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
    }
    setShowFullscreenSearch(false);
  };
  
  // Filtrer les artisans selon la recherche
  const filteredArtisans = artisans.filter(a => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        a.shop_name?.toLowerCase().includes(query) ||
        a.metier?.toLowerCase().includes(query) ||
        a.city?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Trier par proximité quand la géolocalisation est disponible
  const haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371, toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };
  const sortedArtisans = userCoords
    ? [...filteredArtisans].sort((a, b) => {
        if (!a.latitude) return 1;
        if (!b.latitude) return -1;
        return haversineKm(userCoords.lat, userCoords.lng, a.latitude, a.longitude)
             - haversineKm(userCoords.lat, userCoords.lng, b.latitude, b.longitude);
      })
    : filteredArtisans;
  
  // Rafraîchir
  const handleRefresh = async () => {
    setRefreshing(true);
    // Simuler un chargement
    await new Promise(resolve => setTimeout(resolve, 500));
    setRefreshing(false);
  };
  
  // Naviguer vers la fiche artisan
  const handleArtisanClick = (artisan) => {
    navigate(`/artisans/${artisan.id}`);
  };
  
  // Ouvrir le formulaire Devis inline
  const handleDevisClick = () => {
    setFilterTab('devis');
    setDevisStep(1);
    setDevisSuccess(false);
  };
  
  // Fermer le formulaire Devis et revenir à la liste
  const handleCloseDevis = () => {
    setFilterTab('tous');
    setDevisStep(1);
    setDevisFormData({
      type: '', description: '', urgence: '', budget: '',
      ville: '', codePostal: '', nom: '', telephone: '', email: '',
    });
    setDevisSuccess(false);
  };
  
  // Mise à jour du formulaire devis
  const updateDevisForm = (field, value) => {
    setDevisFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Navigation étapes devis
  const nextDevisStep = () => {
    if (devisStep < totalDevisSteps) setDevisStep(devisStep + 1);
  };
  const prevDevisStep = () => {
    if (devisStep > 1) setDevisStep(devisStep - 1);
  };
  
  // Soumission du devis
  const handleSubmitDevis = async () => {
    setIsSubmittingDevis(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setDevisSuccess(true);
      toast.success('Ta demande a été envoyée aux artisans !');
    } catch (error) {
      toast.error('Une erreur est survenue');
    } finally {
      setIsSubmittingDevis(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingBottom: '80px' }}>
      {/* Header Artisans - Simple et propre */}
      <header className="bg-white border-b border-gray-200 sticky top-[94px] z-50">
        <div className="flex items-center justify-between h-14 px-3">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-[#1E3A5F]">Artisans</h1>
            {userCoords && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                <MapPin size={9} />
                Près de vous
              </span>
            )}
          </div>

          {/* Boutons droite : refresh + loupe */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowFullscreenSearch(true)}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="artisans-search-btn"
            >
              <Search size={20} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Onglets : Carte / Filtres / Devis gratuit */}
        <div className="flex border-b border-gray-100 bg-white">
          <button
            onClick={() => setShowMapInline(true)}
            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              showMapInline
                ? 'text-orange-600 border-b-2 border-orange-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="artisans-tab-carte"
          >
            <MapPin size={14} />
            Carte
          </button>
          <button
            onClick={() => setShowFullFilters(true)}
            className="flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 text-gray-500 hover:text-[#1E3A5F] transition-colors"
            data-testid="artisans-tab-filtre"
          >
            <SlidersHorizontal size={14} />
            Filtres
          </button>
          <button
            onClick={handleDevisClick}
            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              filterTab === 'devis'
                ? 'text-emerald-600 border-b-2 border-emerald-500'
                : 'text-emerald-600 hover:bg-emerald-50'
            }`}
            data-testid="artisans-tab-devis"
          >
            <Gift size={14} />
            Devis gratuit
          </button>
        </div>
      </header>
      
      {/* ============ CARTE PLEIN ÉCRAN - OVERLAY FADE ============ */}
      <AnimatePresence>
        {showMapInline && (
          <>
            {/* Fond fade-in (ne crée pas de stacking context sur la carte) */}
            <motion.div
              key="carte-bg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ position: 'fixed', inset: 0, zIndex: 9997, background: '#F8FAFC', pointerEvents: 'none' }}
            />

            {/* Carte plein écran - positionnée indépendamment */}
            <ArtisansMapView
              key="carte-map"
              mobileMode={true}
              onBack={() => setShowMapInline(false)}
              data-testid="carte-artisans-fullscreen"
            />

            {/* Bottom sheet flottant - slide-up */}
            <motion.div
              key="carte-sheet"
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ type: 'spring', damping: 26, stiffness: 200, delay: 0.12 }}
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 9999,
                background: 'white',
                paddingTop: '12px',
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingBottom: 'max(calc(env(safe-area-inset-bottom) + 12px), 16px)',
                borderTop: '1px solid #F3F4F6',
                boxShadow: '0 -4px 24px rgba(0,0,0,0.10)',
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {filteredArtisans.length} artisan{filteredArtisans.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-gray-500">Près de chez vous</p>
                </div>
                <button
                  onClick={() => setShowMapInline(false)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#1E3A5F] text-white rounded-xl text-sm font-semibold active:scale-[0.97] transition-all"
                >
                  Voir la liste
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* ============ CONTENU PRINCIPAL - Liste Artisans (masqué si carte active) ============ */}
      {!showMapInline && (
      <div className="p-3">
          <AnimatePresence mode="wait">
          {/* ============ FORMULAIRE DEVIS → overlay fullscreen ============ */}
          {/* devis form déplacé dans l'overlay plein écran ci-dessous */}
          {false ? (
            <motion.div
              key="devis-form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              data-testid="devis-inline-form"
            >
              {/* Écran de succès */}
              {devisSuccess ? (
                <div className="text-center py-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', duration: 0.5 }}
                    className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center"
                  >
                    <CheckCircle2 size={40} className="text-emerald-500" />
                  </motion.div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Tu recevras jusqu'à <span className="font-semibold text-emerald-600">5 devis gratuits</span> sous 48h !
                  </p>
                  <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8F] rounded-xl p-4 text-white mb-4">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Gift size={18} />
                      <span className="font-bold text-sm">Ce qui nous différencie</span>
                    </div>
                    <p className="text-white/90 text-xs">
                      Chez AbracadaBati, <span className="font-bold text-yellow-300">les artisans ne paient pas les leads</span>. 
                      Ils peuvent donc te proposer des devis plus compétitifs !
                    </p>
                  </div>
                  <Button
                    onClick={handleCloseDevis}
                    className="bg-orange-500 hover:bg-orange-600 rounded-xl h-10 px-6"
                    data-testid="devis-back-to-artisans"
                  >
                    <Users size={16} className="mr-2" />
                    Voir les artisans
                  </Button>
                </div>
              ) : (
                <>
                  {/* Stepper */}
                  <div className="flex items-center justify-center gap-1.5 mb-4">
                    {Array.from({ length: totalDevisSteps }).map((_, i) => (
                      <div key={i} className="flex items-center">
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-all ${
                            i + 1 === devisStep 
                              ? 'bg-orange-500 text-white scale-110' 
                              : i + 1 < devisStep
                                ? 'bg-emerald-500 text-white'
                                : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {i + 1 < devisStep ? <CheckCircle2 size={14} /> : i + 1}
                        </div>
                        {i < totalDevisSteps - 1 && (
                          <div className={`w-6 h-0.5 mx-0.5 rounded ${i + 1 < devisStep ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Contenu des étapes */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <AnimatePresence mode="wait">
                      {/* ÉTAPE 1 : Type de travaux */}
                      {devisStep === 1 && (
                        <motion.div
                          key="devis-step1"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                        >
                          <h3 className="text-base font-bold text-gray-900 mb-1">Quel type de travaux ?</h3>
                          <p className="text-xs text-gray-500 mb-3">Sélectionne la catégorie principale</p>
                          <div className="grid grid-cols-3 gap-2">
                            {TYPES_TRAVAUX.map((type) => {
                              const Icon = type.icon;
                              const isSelected = devisFormData.type === type.id;
                              return (
                                <button
                                  key={type.id}
                                  onClick={() => updateDevisForm('type', type.id)}
                                  className={`p-2 rounded-lg border-2 transition-all text-center ${
                                    isSelected 
                                      ? 'border-[#1E40AF] bg-[#1E40AF]/5' 
                                      : 'border-gray-100 hover:border-gray-200'
                                  }`}
                                  data-testid={`devis-type-${type.id}`}
                                >
                                  <div 
                                    className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-1"
                                    style={{ backgroundColor: `${type.color}15` }}
                                  >
                                    <Icon size={16} style={{ color: type.color }} />
                                  </div>
                                  <p className="font-medium text-gray-900 text-[10px] leading-tight">{type.label}</p>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}

                      {/* ÉTAPE 2 : Description */}
                      {devisStep === 2 && (
                        <motion.div
                          key="devis-step2"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                        >
                          <h3 className="text-base font-bold text-gray-900 mb-1">Décris ton projet</h3>
                          <p className="text-xs text-gray-500 mb-3">Plus tu es précis, meilleurs seront les devis</p>
                          <textarea
                            value={devisFormData.description}
                            onChange={(e) => updateDevisForm('description', e.target.value)}
                            placeholder="Ex: Je souhaite rénover ma salle de bain de 6m². Remplacer la baignoire par une douche à l'italienne..."
                            className="w-full h-28 p-3 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none"
                            data-testid="devis-description"
                          />
                          <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                            <p className="text-[10px] font-medium text-blue-800">💡 Conseils : dimensions (m²), matériaux souhaités, fourniture du matériel</p>
                          </div>
                        </motion.div>
                      )}

                      {/* ÉTAPE 3 : Urgence et Budget */}
                      {devisStep === 3 && (
                        <motion.div
                          key="devis-step3"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                        >
                          <h3 className="text-base font-bold text-gray-900 mb-3">Urgence et budget</h3>
                          
                          {/* Urgence */}
                          <div className="mb-4">
                            <p className="font-medium text-gray-700 text-xs mb-2 flex items-center gap-1">
                              <Clock size={12} />
                              Quand souhaites-tu commencer ?
                            </p>
                            <div className="grid grid-cols-4 gap-2">
                              {URGENCES.map((u) => {
                                const isSelected = devisFormData.urgence === u.id;
                                return (
                                  <button
                                    key={u.id}
                                    onClick={() => updateDevisForm('urgence', u.id)}
                                    className={`p-2 rounded-lg border-2 transition-all text-center ${
                                      isSelected 
                                        ? 'border-[#1E40AF] bg-[#1E40AF]/5' 
                                        : 'border-gray-100 hover:border-gray-200'
                                    }`}
                                    data-testid={`devis-urgence-${u.id}`}
                                  >
                                    <div 
                                      className="w-2 h-2 rounded-full mx-auto mb-1"
                                      style={{ backgroundColor: u.color }}
                                    />
                                    <p className="font-medium text-gray-900 text-[10px]">{u.label}</p>
                                    <p className="text-[9px] text-gray-500">{u.sublabel}</p>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          
                          {/* Budget */}
                          <div>
                            <p className="font-medium text-gray-700 text-xs mb-2 flex items-center gap-1">
                              <Euro size={12} />
                              Quel est ton budget estimé ?
                            </p>
                            <div className="grid grid-cols-3 gap-2">
                              {BUDGETS.map((b) => {
                                const isSelected = devisFormData.budget === b.id;
                                return (
                                  <button
                                    key={b.id}
                                    onClick={() => updateDevisForm('budget', b.id)}
                                    className={`p-2 rounded-lg border-2 transition-all text-center ${
                                      isSelected 
                                        ? 'border-[#1E40AF] bg-[#1E40AF]/5' 
                                        : 'border-gray-100 hover:border-gray-200'
                                    }`}
                                    data-testid={`devis-budget-${b.id}`}
                                  >
                                    <p className="font-semibold text-gray-900 text-xs">{b.range}</p>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* ÉTAPE 4 : Localisation */}
                      {devisStep === 4 && (
                        <motion.div
                          key="devis-step4"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                        >
                          <h3 className="text-base font-bold text-gray-900 mb-1">Où se situe le chantier ?</h3>
                          <p className="text-xs text-gray-500 mb-3">Pour trouver les artisans les plus proches</p>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                <MapPin size={12} className="inline mr-1" />
                                Code postal *
                              </label>
                              <Input
                                type="text"
                                value={devisFormData.codePostal}
                                onChange={(e) => updateDevisForm('codePostal', e.target.value)}
                                placeholder="Ex: 75001"
                                className="h-10 rounded-lg text-sm"
                                data-testid="devis-code-postal"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Ville *</label>
                              <Input
                                type="text"
                                value={devisFormData.ville}
                                onChange={(e) => updateDevisForm('ville', e.target.value)}
                                placeholder="Ex: Paris"
                                className="h-10 rounded-lg text-sm"
                                data-testid="devis-ville"
                              />
                            </div>
                          </div>
                          
                          <div className="mt-3 p-2 bg-emerald-50 rounded-lg flex items-start gap-2">
                            <Shield size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                            <p className="text-[10px] text-emerald-700">
                              Ton adresse exacte n'est jamais partagée avec les artisans.
                            </p>
                          </div>
                        </motion.div>
                      )}

                      {/* ÉTAPE 5 : Coordonnées */}
                      {devisStep === 5 && (
                        <motion.div
                          key="devis-step5"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                        >
                          <h3 className="text-base font-bold text-gray-900 mb-1">Tes coordonnées</h3>
                          <p className="text-xs text-gray-500 mb-3">Pour recevoir les devis des artisans</p>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Ton nom *</label>
                              <Input
                                type="text"
                                value={devisFormData.nom}
                                onChange={(e) => updateDevisForm('nom', e.target.value)}
                                placeholder="Ex: Jean Dupont"
                                className="h-10 rounded-lg text-sm"
                                data-testid="devis-nom"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                <Phone size={12} className="inline mr-1" />
                                Téléphone *
                              </label>
                              <Input
                                type="tel"
                                value={devisFormData.telephone}
                                onChange={(e) => updateDevisForm('telephone', e.target.value)}
                                placeholder="Ex: 06 12 34 56 78"
                                className="h-10 rounded-lg text-sm"
                                data-testid="devis-telephone"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                <Mail size={12} className="inline mr-1" />
                                Email *
                              </label>
                              <Input
                                type="email"
                                value={devisFormData.email}
                                onChange={(e) => updateDevisForm('email', e.target.value)}
                                placeholder="Ex: jean.dupont@email.com"
                                className="h-10 rounded-lg text-sm"
                                data-testid="devis-email"
                              />
                            </div>
                          </div>

                          {/* Avertissement Démonstration */}
                          <div className="mt-3 p-2 bg-amber-50 border border-amber-300 rounded-lg">
                            <div className="flex items-start gap-2">
                              <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-bold text-[10px]">!</span>
                              </div>
                              <div>
                                <p className="font-bold text-amber-800 text-[10px]">Mode Démonstration</p>
                                <p className="text-[9px] text-amber-700">
                                  Version bêta. Lancement officiel le <span className="font-bold">1er juillet 2026</span>.
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Récap */}
                          <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                            <p className="font-medium text-gray-900 text-xs mb-1">Récapitulatif</p>
                            <div className="text-[10px] text-gray-600 space-y-0.5">
                              <p>• Type : <span className="font-medium">{TYPES_TRAVAUX.find(t => t.id === devisFormData.type)?.label || '-'}</span></p>
                              <p>• Urgence : <span className="font-medium">{URGENCES.find(u => u.id === devisFormData.urgence)?.label || '-'}</span></p>
                              <p>• Budget : <span className="font-medium">{BUDGETS.find(b => b.id === devisFormData.budget)?.label || '-'}</span></p>
                              <p>• Lieu : <span className="font-medium">{devisFormData.codePostal} {devisFormData.ville}</span></p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Navigation étapes */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                      <Button
                        variant="outline"
                        onClick={devisStep === 1 ? handleCloseDevis : prevDevisStep}
                        className="rounded-lg h-9 px-4 text-xs"
                        data-testid="devis-btn-back"
                      >
                        <ArrowLeft size={14} className="mr-1" />
                        {devisStep === 1 ? 'Annuler' : 'Retour'}
                      </Button>
                      
                      {devisStep < totalDevisSteps ? (
                        <Button
                          onClick={nextDevisStep}
                          disabled={
                            (devisStep === 1 && !devisFormData.type) ||
                            (devisStep === 2 && !devisFormData.description) ||
                            (devisStep === 3 && (!devisFormData.urgence || !devisFormData.budget)) ||
                            (devisStep === 4 && (!devisFormData.ville || !devisFormData.codePostal))
                          }
                          className="bg-orange-500 hover:bg-orange-600 rounded-lg h-9 px-4 text-xs"
                          data-testid="devis-btn-next"
                        >
                          Continuer
                          <ArrowRight size={14} className="ml-1" />
                        </Button>
                      ) : (
                        <Button
                          onClick={handleSubmitDevis}
                          disabled={isSubmittingDevis || !devisFormData.nom || !devisFormData.telephone || !devisFormData.email}
                          className="bg-emerald-500 hover:bg-emerald-600 rounded-lg h-9 px-4 text-xs"
                          data-testid="devis-btn-submit"
                        >
                          {isSubmittingDevis ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                              Envoi...
                            </>
                          ) : (
                            <>
                              <Send size={14} className="mr-1" />
                              Envoyer
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Badge différenciateur */}
                  <div className="mt-3 bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-xl p-3 text-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                        <Gift size={20} />
                      </div>
                      <div>
                        <p className="font-bold text-xs mb-0.5">Pourquoi AbracadaBati ?</p>
                        <p className="text-white/90 text-[10px] leading-tight">
                          <span className="font-bold text-yellow-300">Nos artisans ne paient pas les leads</span> = des prix plus justes pour toi !
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          ) : (
            /* ============ LISTE ARTISANS ============ */
            <motion.div
              key="artisans-list"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Compteur résultats */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-500">
                  {sortedArtisans.length} artisan{sortedArtisans.length > 1 ? 's' : ''}
                  {userCoords && !searchQuery ? ' près de vous' : searchQuery ? ` pour "${searchQuery}"` : ''}
                </p>
                {searchQuery && (
                  <button 
                    onClick={() => { setSearchQuery(''); }}
                    className="text-xs text-orange-600 font-medium"
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                </div>
              ) : sortedArtisans.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center px-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Users size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    Aucun artisan trouvé
                  </h3>
                  <p className="text-sm text-gray-500">
                    {searchQuery 
                      ? `Aucun résultat pour "${searchQuery}"`
                      : 'Modifie tes critères de recherche'}
                  </p>
                </div>
              ) : (
                /* Grille Artisans */
                <div className="grid grid-cols-2 gap-3">
                  {sortedArtisans.map((artisan) => (
                    <ArtisanCard 
                      key={artisan.id} 
                      artisan={artisan} 
                      onClick={handleArtisanClick}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      )}
      
      {/* ══════════════════════════════════════════════════════════════
          RECHERCHE FULLSCREEN - "Trouve ton artisan en 3 clics"
      ══════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#ffffff',
              zIndex: 99999,
            }}
          >
            {/* X fermer */}
            <div 
              className="flex items-center justify-end"
              style={{ 
                padding: '8px 12px',
                paddingTop: 'max(8px, env(safe-area-inset-top, 8px))',
              }}
            >
              <button
                onClick={() => setShowFullscreenSearch(false)}
                style={{ width: '32px', height: '32px' }}
                data-testid="fullscreen-search-close"
              >
                <X size={24} className="text-[#64748B]" strokeWidth={2} />
              </button>
            </div>
            
            {/* Barre de recherche - Style stadium avec loupe orange */}
            <div className="px-3 pb-3">
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSearchSubmit(searchQuery);
                }}
                className="flex items-center w-full"
                style={{
                  height: '56px',
                  border: '2px solid #E8650A',
                  borderRadius: '28px',
                  background: '#F8F9FA',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* Loupe orange à gauche */}
                <motion.button
                  type="submit"
                  initial={{ x: 320, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 280, damping: 24, delay: 0.03 }}
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ 
                    width: '56px', 
                    height: '56px',
                    background: '#E8650A',
                    borderRadius: '26px 0 0 26px',
                    position: 'relative',
                    left: '-2px',
                  }}
                  data-testid="fullscreen-search-submit"
                >
                  <Search size={24} className="text-white" strokeWidth={2} />
                </motion.button>
                
                {/* Input */}
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Artisan, métier, ville..."
                  className="flex-1 bg-transparent text-[#333] placeholder-[#999] focus:outline-none"
                  style={{ fontSize: '16px', paddingLeft: '12px', paddingRight: '16px' }}
                  autoFocus
                  data-testid="fullscreen-search-input"
                />
                
                {/* Clear */}
                {searchQuery && (
                  <button 
                    type="button" 
                    onClick={() => setSearchQuery('')}
                    className="flex items-center justify-center mr-4"
                    style={{ width: '22px', height: '22px', background: '#C9CDD4', borderRadius: '50%' }}
                  >
                    <X size={14} className="text-white" strokeWidth={2.5} />
                  </button>
                )}
              </form>
            </div>
            
            {/* Bouton "Trouve ton artisan en 3 clics" */}
            <motion.div 
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="px-4 py-4"
            >
              <button
                onClick={() => {
                  setShowFullscreenSearch(false);
                  setTimeout(() => setShowFullFilters(true), 150);
                }}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-semibold text-white transition-all active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
                  boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)',
                }}
                data-testid="mobile-search-open-filters"
              >
                <SlidersHorizontal size={20} strokeWidth={2} />
                <span style={{ fontSize: '15px' }}>Trouve ton artisan en 3 clics</span>
              </button>
            </motion.div>
            
            {/* Contenu - Recherches récentes */}
            <div className="p-4 bg-white flex-1 overflow-y-auto">
              <p className="text-[11px] text-[#E8650A] uppercase font-semibold tracking-wide mb-4">
                Recherche dans : Artisans
              </p>
              
              {/* Recherches récentes */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold tracking-wide">
                    Recherches récentes
                  </p>
                  {recentSearches.length > 0 && (
                    <button
                      onClick={clearRecentSearches}
                      className="text-[11px] text-[#E8650A] font-medium"
                    >
                      Effacer
                    </button>
                  )}
                </div>
                
                {recentSearches.length > 0 ? (
                  <div className="space-y-1">
                    {recentSearches.map((search, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSearchSubmit(search)}
                        className="w-full flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Clock size={16} className="text-[#9CA3AF]" />
                        <span className="text-[14px] text-[#374151]">{search}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search size={32} className="text-[#E5E7EB] mb-3" />
                    <p className="text-[#9CA3AF] text-sm">Aucune recherche récente</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* ══════════════════════════════════════════════════════════════
          DEVIS GRATUIT - OVERLAY PLEIN ÉCRAN (SLIDE-UP)
      ══════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {filterTab === 'devis' && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 220 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background: '#F8FAFC',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* ── HEADER ── */}
            <div
              style={{
                paddingTop: 'max(env(safe-area-inset-top), 12px)',
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingBottom: '12px',
                background: 'white',
                borderBottom: '1px solid #F3F4F6',
                flexShrink: 0,
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-bold text-gray-900">Décris ton projet</h2>
                  <p className="text-xs text-gray-500">
                    Reçois jusqu'à <span className="font-semibold text-orange-500">5 devis gratuits</span> d'artisans
                  </p>
                </div>
                <button
                  onClick={handleCloseDevis}
                  className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center active:scale-95 transition-all"
                >
                  <X size={18} className="text-gray-600" />
                </button>
              </div>
            </div>

            {/* ── BARRE DE PROGRESSION ── */}
            <div className="flex-shrink-0 bg-gray-100" style={{ height: '3px' }}>
              <motion.div
                style={{ height: '3px', backgroundColor: '#E8650A' }}
                initial={false}
                animate={{ width: `${(devisStep / totalDevisSteps) * 100}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>

            {/* ── INDICATEUR ÉTAPE ── */}
            <div className="flex-shrink-0 px-4 pt-2.5 pb-1 flex items-center justify-between">
              <span className="text-xs font-semibold text-orange-500">
                Étape {devisStep} / {totalDevisSteps}
              </span>
              {devisStep > 1 && !devisSuccess && (
                <button
                  onClick={prevDevisStep}
                  className="flex items-center gap-1 text-xs text-gray-500 active:opacity-70"
                >
                  <ArrowLeft size={12} />
                  Retour
                </button>
              )}
            </div>

            {/* ── CORPS SCROLLABLE ── */}
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {devisSuccess ? (
                <div className="text-center py-12">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', duration: 0.5 }}
                    className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center"
                  >
                    <CheckCircle2 size={40} className="text-emerald-500" />
                  </motion.div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Demande envoyée !</h2>
                  <p className="text-sm text-gray-600 mb-6">
                    Tu recevras jusqu'à <span className="font-semibold text-emerald-600">5 devis gratuits</span> sous 48h !
                  </p>
                  <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8F] rounded-2xl p-5 text-white mb-6">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Gift size={20} />
                      <span className="font-bold text-sm">Ce qui nous différencie</span>
                    </div>
                    <p className="text-white/90 text-sm">
                      Chez AbracadaBati, <span className="font-bold text-yellow-300">les artisans ne paient pas les leads</span>.
                      Ils peuvent donc te proposer des devis plus compétitifs !
                    </p>
                  </div>
                  <Button
                    onClick={handleCloseDevis}
                    className="bg-orange-500 hover:bg-orange-600 rounded-xl h-12 px-8"
                    data-testid="devis-back-to-artisans"
                  >
                    <Users size={16} className="mr-2" />
                    Voir les artisans
                  </Button>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  {/* ÉTAPE 1 : Type de travaux */}
                  {devisStep === 1 && (
                    <motion.div
                      key="fs-step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <h3 className="text-base font-bold text-gray-900 mb-1">Quel type de travaux ?</h3>
                      <p className="text-xs text-gray-500 mb-3">Sélectionne la catégorie principale</p>
                      <div className="grid grid-cols-3 gap-2">
                        {TYPES_TRAVAUX.map((type) => {
                          const Icon = type.icon;
                          const isSelected = devisFormData.type === type.id;
                          return (
                            <button
                              key={type.id}
                              onClick={() => updateDevisForm('type', type.id)}
                              className={`p-2.5 rounded-xl border-2 transition-all text-center ${
                                isSelected
                                  ? 'border-[#1E40AF] bg-[#1E40AF]/5'
                                  : 'border-gray-100 hover:border-gray-200 bg-white'
                              }`}
                              data-testid={`devis-type-${type.id}`}
                            >
                              <div
                                className="w-9 h-9 rounded-lg flex items-center justify-center mx-auto mb-1.5"
                                style={{ backgroundColor: `${type.color}15` }}
                              >
                                <Icon size={18} style={{ color: type.color }} />
                              </div>
                              <p className="font-medium text-gray-900 text-[11px] leading-tight">{type.label}</p>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* ÉTAPE 2 : Description */}
                  {devisStep === 2 && (
                    <motion.div
                      key="fs-step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <h3 className="text-base font-bold text-gray-900 mb-1">Décris ton projet</h3>
                      <p className="text-xs text-gray-500 mb-3">Plus tu es précis, meilleurs seront les devis</p>
                      <textarea
                        value={devisFormData.description}
                        onChange={(e) => updateDevisForm('description', e.target.value)}
                        placeholder="Ex: Je souhaite rénover ma salle de bain de 6m². Remplacer la baignoire par une douche à l'italienne..."
                        className="w-full h-36 p-3 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none bg-white"
                        data-testid="devis-description"
                      />
                      <div className="mt-2 p-2.5 bg-blue-50 rounded-xl">
                        <p className="text-[11px] font-medium text-blue-800">💡 Conseils : dimensions (m²), matériaux souhaités, fourniture du matériel</p>
                      </div>
                    </motion.div>
                  )}

                  {/* ÉTAPE 3 : Urgence et Budget */}
                  {devisStep === 3 && (
                    <motion.div
                      key="fs-step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <h3 className="text-base font-bold text-gray-900 mb-3">Urgence et budget</h3>
                      <div className="mb-4">
                        <p className="font-medium text-gray-700 text-xs mb-2 flex items-center gap-1">
                          <Clock size={12} />
                          Quand souhaites-tu commencer ?
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {URGENCES.map((u) => {
                            const isSelected = devisFormData.urgence === u.id;
                            return (
                              <button
                                key={u.id}
                                onClick={() => updateDevisForm('urgence', u.id)}
                                className={`p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                                  isSelected
                                    ? 'border-[#1E40AF] bg-[#1E40AF]/5'
                                    : 'border-gray-100 hover:border-gray-200 bg-white'
                                }`}
                                data-testid={`devis-urgence-${u.id}`}
                              >
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: u.color }} />
                                <div className="text-left">
                                  <p className="font-semibold text-gray-900 text-xs">{u.label}</p>
                                  <p className="text-[10px] text-gray-500">{u.sublabel}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700 text-xs mb-2 flex items-center gap-1">
                          <Euro size={12} />
                          Quel est ton budget estimé ?
                        </p>
                        <div className="space-y-2">
                          {BUDGETS.map((b) => {
                            const isSelected = devisFormData.budget === b.id;
                            return (
                              <button
                                key={b.id}
                                onClick={() => updateDevisForm('budget', b.id)}
                                className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                                  isSelected
                                    ? 'border-[#1E40AF] bg-[#1E40AF]/5'
                                    : 'border-gray-100 hover:border-gray-200 bg-white'
                                }`}
                                data-testid={`devis-budget-${b.id}`}
                              >
                                <p className="font-semibold text-gray-900 text-sm">{b.label}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* ÉTAPE 4 : Localisation */}
                  {devisStep === 4 && (
                    <motion.div
                      key="fs-step4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <h3 className="text-base font-bold text-gray-900 mb-1">Où se situe le chantier ?</h3>
                      <p className="text-xs text-gray-500 mb-4">Pour trouver les artisans les plus proches</p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            <MapPin size={12} className="inline mr-1" />
                            Code postal *
                          </label>
                          <Input
                            type="text"
                            value={devisFormData.codePostal}
                            onChange={(e) => updateDevisForm('codePostal', e.target.value)}
                            placeholder="Ex: 75001"
                            className="h-12 rounded-xl text-sm"
                            data-testid="devis-code-postal"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Ville *</label>
                          <Input
                            type="text"
                            value={devisFormData.ville}
                            onChange={(e) => updateDevisForm('ville', e.target.value)}
                            placeholder="Ex: Paris"
                            className="h-12 rounded-xl text-sm"
                            data-testid="devis-ville"
                          />
                        </div>
                      </div>
                      <div className="mt-4 p-3 bg-emerald-50 rounded-xl flex items-start gap-2">
                        <Shield size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] text-emerald-700">Ton adresse exacte n'est jamais partagée avec les artisans.</p>
                      </div>
                    </motion.div>
                  )}

                  {/* ÉTAPE 5 : Coordonnées */}
                  {devisStep === 5 && (
                    <motion.div
                      key="fs-step5"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <h3 className="text-base font-bold text-gray-900 mb-1">Tes coordonnées</h3>
                      <p className="text-xs text-gray-500 mb-4">Pour recevoir les devis des artisans</p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">Ton nom *</label>
                          <Input
                            type="text"
                            value={devisFormData.nom}
                            onChange={(e) => updateDevisForm('nom', e.target.value)}
                            placeholder="Ex: Jean Dupont"
                            className="h-12 rounded-xl text-sm"
                            data-testid="devis-nom"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            <Phone size={12} className="inline mr-1" />
                            Téléphone *
                          </label>
                          <Input
                            type="tel"
                            value={devisFormData.telephone}
                            onChange={(e) => updateDevisForm('telephone', e.target.value)}
                            placeholder="Ex: 06 12 34 56 78"
                            className="h-12 rounded-xl text-sm"
                            data-testid="devis-telephone"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            <Mail size={12} className="inline mr-1" />
                            Email *
                          </label>
                          <Input
                            type="email"
                            value={devisFormData.email}
                            onChange={(e) => updateDevisForm('email', e.target.value)}
                            placeholder="Ex: jean.dupont@email.com"
                            className="h-12 rounded-xl text-sm"
                            data-testid="devis-email"
                          />
                        </div>
                      </div>
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-[10px]">!</span>
                          </div>
                          <div>
                            <p className="font-bold text-amber-800 text-[11px]">Mode Démonstration</p>
                            <p className="text-[10px] text-amber-700">Version bêta. Lancement le <span className="font-bold">1er juillet 2026</span>.</p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                        <p className="font-semibold text-gray-900 text-xs mb-2">Récapitulatif</p>
                        <div className="text-[11px] text-gray-600 space-y-1">
                          <p>• Type : <span className="font-medium">{TYPES_TRAVAUX.find(t => t.id === devisFormData.type)?.label || '-'}</span></p>
                          <p>• Urgence : <span className="font-medium">{URGENCES.find(u => u.id === devisFormData.urgence)?.label || '-'}</span></p>
                          <p>• Budget : <span className="font-medium">{BUDGETS.find(b => b.id === devisFormData.budget)?.label || '-'}</span></p>
                          <p>• Lieu : <span className="font-medium">{devisFormData.codePostal} {devisFormData.ville}</span></p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

            </div>

            {/* ── FOOTER STICKY - CTA Continuer/Envoyer ── */}
            {!devisSuccess && (
              <div
                className="flex-shrink-0 bg-white border-t border-gray-100 px-4 pt-3"
                style={{ paddingBottom: 'max(calc(env(safe-area-inset-bottom) + 12px), 16px)' }}
              >
                {devisStep < totalDevisSteps ? (
                  <button
                    onClick={nextDevisStep}
                    disabled={
                      (devisStep === 1 && !devisFormData.type) ||
                      (devisStep === 2 && !devisFormData.description) ||
                      (devisStep === 3 && (!devisFormData.urgence || !devisFormData.budget)) ||
                      (devisStep === 4 && (!devisFormData.ville || !devisFormData.codePostal))
                    }
                    className="w-full h-14 bg-[#E8650A] hover:bg-[#D55A09] disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold text-base rounded-xl shadow-lg shadow-[#E8650A]/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                    data-testid="devis-btn-next"
                  >
                    Continuer
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitDevis}
                    disabled={isSubmittingDevis || !devisFormData.nom || !devisFormData.telephone || !devisFormData.email}
                    className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold text-base rounded-xl shadow-lg shadow-emerald-500/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                    data-testid="devis-btn-submit"
                  >
                    {isSubmittingDevis ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Envoyer ma demande
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* UnifiedFilterSystem - système de filtres complet */}
      <UnifiedFilterSystem 
        context="artisans"
        isOpen={showFullFilters}
        onClose={() => setShowFullFilters(false)}
        filters={activeFilters}
        onFiltersChange={setActiveFilters}
        resultsCount={filteredArtisans.length}
        onApply={(filters) => {
          // Appliquer les filtres
          if (filters.metiers?.length) {
            const metierLabels = filters.metiers.map(id => {
              const found = METIERS_SEARCH.find(m => m.query.toLowerCase() === id);
              return found?.query || id;
            });
            setSearchQuery(metierLabels.join(', '));
          }
          setShowFullFilters(false);
        }}
      />
      
      {/* Bottom Nav */}
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
    </div>
  );
}
